import React, { useEffect, useRef, useState } from "react";

/**
 * Counts up/down from the currently displayed value to `value` over `duration` ms
 * using requestAnimationFrame. No external library — plain React state + rAF.
 *
 * Guards against the animation ever getting stuck on a stale number (e.g. if a
 * frame callback is cancelled mid-flight by a fast-following prop update): a
 * fallback timeout always snaps to the exact target shortly after `duration`.
 */
const AnimatedCounter = ({ value = 0, duration = 600 }) => {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const displayRef = useRef(target);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    const from = displayRef.current;
    if (from === target) return undefined;

    const start = performance.now();
    let cancelled = false;

    const tick = (now) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    // Safety net: guarantee the exact target is shown even if the rAF chain
    // gets interrupted (e.g. tab backgrounded, or a rapid StrictMode re-run).
    timeoutRef.current = setTimeout(() => {
      if (!cancelled) setDisplay(target);
    }, duration + 100);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return <>{display}</>;
};

export default AnimatedCounter;
